import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Chip,
    Card,
    CardContent,
    Divider,
    Tooltip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Close,
    Download,
    Share,
    Edit,
    Delete,
    Favorite,
    FavoriteBorder,
    ZoomIn,
    ZoomOut,
    RotateLeft,
    RotateRight,
    Info,
    Telegram,
} from '@mui/icons-material';
import { Image, apiService } from '../services/api';

// Use the same global cache as ImageGallery
const imageCache = new Map<string, { data: string | null; loading: boolean; promise?: Promise<string | null> }>();

interface DetailedImageViewProps {
    image: Image | null;
    open: boolean;
    onClose: () => void;
    onEdit: (image: Image) => void;
    onDelete: (imageId: number) => void;
    getImageUrl: (filename: string) => string;
    isGuest?: boolean;
    hasPermission?: (permission: string) => boolean;
}

const DetailedImageView: React.FC<DetailedImageViewProps> = ({
    image,
    open,
    onClose,
    onEdit,
    onDelete,
    getImageUrl,
    isGuest = false,
    hasPermission = () => true, // Default to allow all if not provided
}) => {
    const theme = useTheme();
    const readOnly = isGuest && !hasPermission('write');
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isFavorite, setIsFavorite] = useState(false);
    const [zoom, setZoom] = useState(100);
    const [rotation, setRotation] = useState(0);
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [imageLoading, setImageLoading] = useState(false);

    // Fetch actual image data when dialog opens - with caching
    useEffect(() => {
        if (!image || !open) {
            return;
        }

        // Check cache first
        const cached = imageCache.get(image.filename);
        if (cached?.data) {
            setImageDataUrl(cached.data);
            setImageLoading(false);
            return;
        }

        const fetchImageData = async () => {
            // Check if already loading
            if (cached?.loading && cached?.promise) {
                try {
                    const data = await cached.promise;
                    if (data) {
                        setImageDataUrl(data);
                    }
                } catch (error) {
                    console.error('Error waiting for cached promise:', error);
                }
                setImageLoading(false);
                return;
            }

            setImageLoading(true);

            const fetchPromise = (async () => {
                try {
                    const token = localStorage.getItem('admin_token') ||
                        apiService.getAccessToken() ||
                        localStorage.getItem('access_token');

                    const response = await fetch(`https://pucollection.com/api/image-data/${image.filename}?token=${token}`);
                    const data = await response.json();

                    if (data.success) {
                        // Cache the result
                        imageCache.set(image.filename, { data: data.data_url, loading: false });
                        setImageDataUrl(data.data_url);
                        return data.data_url;
                    } else {
                        console.error('Failed to fetch image data:', data.error);
                        imageCache.set(image.filename, { data: null, loading: false });
                        return null;
                    }
                } catch (error) {
                    console.error('Error fetching image data:', error);
                    imageCache.set(image.filename, { data: null, loading: false });
                    return null;
                } finally {
                    setImageLoading(false);
                }
            })();

            // Cache the loading promise
            imageCache.set(image.filename, { data: null, loading: true, promise: fetchPromise });
            await fetchPromise;
        };

        fetchImageData();
    }, [image, open]);

    if (!image) return null;

    const handleDownload = () => {
        if (imageDataUrl) {
            const link = document.createElement('a');
            link.href = imageDataUrl;
            link.download = image.original_filename;
            link.click();
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: image.title,
                    text: image.description,
                    url: window.location.href,
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(window.location.href);
            // You could show a toast notification here
        }
    };

    const handleTelegramShare = async () => {
        try {
            console.log('🔵 Starting Telegram share process...');

            // Prepare comprehensive image data for bot integration
            const imageData = {
                id: image.id,
                filename: image.filename,
                title: image.title,
                description: image.description,
                category: image.category,
                tags: image.tags.map(tag => tag.name),
                primaryTag: image.primary_tag?.name || null,
                fileSize: image.file_size,
                dimensions: image.width && image.height ? `${image.width}x${image.height}` : null,
                // Physical properties
                length: image.length,
                maxThickness: image.max_thickness,
                fullyInsertable: image.fully_insertable,
                softness: image.softness,
                // URLs for bot access
                imageUrl: getImageUrl(image.filename),
                webUrl: window.location.href,
                createdAt: image.created_at
            };

            console.log('🔵 Sharing to Telegram...', imageData);

            const token = apiService.getAccessToken() || localStorage.getItem('admin_token') || '';

            let response: Response | undefined;
            let result: any = {};

            if (token) {
                console.log('🔵 Trying authenticated endpoint...');
                response = await fetch('https://pucollection.com/api/telegram/share', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(imageData)
                });

                result = await response.json();
                console.log('🔵 Authenticated response:', response.status, result);
            }

            if (response?.ok && result.success) {
                alert('✅ Image successfully shared to Telegram!');
                console.log('✅ Telegram share successful:', result);
            } else {
                const errorMsg = result.error || 'Failed to share to Telegram';
                alert(`❌ Error sharing to Telegram: ${errorMsg}`);
                console.error('❌ Telegram share failed:', result);
            }

        } catch (error) {
            console.error('❌ Failed to share to Telegram:', error);
            alert(`❌ Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 25, 300));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 25, 25));
    };

    const handleRotateLeft = () => {
        setRotation(prev => prev - 90);
    };

    const handleRotateRight = () => {
        setRotation(prev => prev + 90);
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    height: isMobile ? '100%' : '90vh',
                    maxHeight: isMobile ? '100%' : '90vh'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                        {image.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Action Buttons - Show based on permissions */}

                        {/* Download - only for admins or users with write permission */}
                        {(!isGuest || hasPermission('write')) && (
                            <Tooltip title="Download">
                                <IconButton onClick={handleDownload} size="small">
                                    <Download />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Share - only for admins or users with write permission */}
                        {(!isGuest || hasPermission('write')) && (
                            <Tooltip title="Share">
                                <IconButton onClick={handleShare} size="small">
                                    <Share />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Telegram - only if user has telegram permission */}
                        {hasPermission('telegram') && (
                            <Tooltip title="Share to Telegram">
                                <IconButton onClick={handleTelegramShare} size="small" color="info">
                                    <Telegram />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Favorite - only for admins */}
                        {!isGuest && (
                            <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
                                <IconButton
                                    onClick={() => setIsFavorite(!isFavorite)}
                                    size="small"
                                    color={isFavorite ? "error" : "default"}
                                >
                                    {isFavorite ? <Favorite /> : <FavoriteBorder />}
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Edit - only for admins or users with write permission */}
                        {(!isGuest || hasPermission('write')) && (
                            <Tooltip title="Edit">
                                <IconButton onClick={() => onEdit(image)} size="small" color="primary">
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Delete - only for admins or users with write permission */}
                        {(!isGuest || hasPermission('write')) && (
                            <Tooltip title="Delete">
                                <IconButton onClick={() => onDelete(image.id)} size="small" color="error">
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Close button - always visible */}
                        <IconButton onClick={onClose} size="small" aria-label="Close details">
                            <Close />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, height: '100%' }}>
                <Box sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    height: "100%"
                }}>
                    {/* Image Display */}
                    <Box sx={{ flex: isMobile ? 'none' : 2, height: isMobile ? '50%' : '100%' }}>
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: '#000',
                                position: 'relative',
                            }}
                        >
                            {/* Image Controls */}
                            {!isMobile && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        zIndex: 1,
                                        display: 'flex',
                                        gap: 1,
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                        borderRadius: 1,
                                        p: 0.5,
                                    }}
                                >
                                    <Tooltip title="Zoom In">
                                        <IconButton onClick={handleZoomIn} size="small" sx={{ color: 'white' }}>
                                            <ZoomIn />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Zoom Out">
                                        <IconButton onClick={handleZoomOut} size="small" sx={{ color: 'white' }}>
                                            <ZoomOut />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="caption" sx={{ color: 'white', alignSelf: 'center', px: 1 }}>
                                        {zoom}%
                                    </Typography>
                                    <Tooltip title="Rotate Left">
                                        <IconButton onClick={handleRotateLeft} size="small" sx={{ color: 'white' }}>
                                            <RotateLeft />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Rotate Right">
                                        <IconButton onClick={handleRotateRight} size="small" sx={{ color: 'white' }}>
                                            <RotateRight />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}

                            {/* Image */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    overflow: 'auto',
                                    p: 2,
                                    position: 'relative',
                                }}
                            >
                                {imageLoading ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <CircularProgress size={60} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading image...
                                        </Typography>
                                    </Box>
                                ) : imageDataUrl ? (
                                    <Box
                                        component="img"
                                        src={imageDataUrl}
                                        alt={image.title}
                                        sx={{
                                            maxWidth: `${zoom}%`,
                                            maxHeight: `${zoom}%`,
                                            objectFit: 'contain',
                                            transform: `rotate(${rotation}deg)`,
                                            transition: 'transform 0.3s ease',
                                        }}
                                    />
                                ) : (
                                    <Typography variant="body1" color="error">
                                        Failed to load image
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Information Panel */}
                    <Box sx={{
                        flex: isMobile ? 'none' : 1,
                        minWidth: isMobile ? 'auto' : 300,
                        height: isMobile ? '50%' : '100%',
                        borderLeft: isMobile ? 'none' : '1px solid',
                        borderTop: isMobile ? '1px solid' : 'none',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{
                            p: isMobile ? 2 : 3,
                            height: '100%',
                            overflow: 'auto'
                        }}>
                            {/* Basic Information */}
                            <Card sx={{ mb: 2 }} elevation={isMobile ? 1 : 2}>
                                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                    <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                                        <Info sx={{ mr: 1, verticalAlign: 'middle', fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                                        Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Description
                                        </Typography>
                                        <Typography variant="body2">
                                            {image.description || 'No description available'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Tag
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            {image.primary_tag ? (
                                                <Chip
                                                    label={image.primary_tag.name}
                                                    size="small"
                                                    sx={{ backgroundColor: image.primary_tag.color, color: 'white' }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No tag assigned
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Technical Details */}
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {readOnly ? 'Physical Properties' : 'Technical Details'}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        {!readOnly && (<>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Dimensions
                                            </Typography>
                                            <Typography variant="body2">
                                                {image.width} × {image.height}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                File Size
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatFileSize(image.file_size)}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Original Name
                                            </Typography>
                                            <Typography variant="body2" noWrap>
                                                {image.original_filename}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Aspect Ratio
                                            </Typography>
                                            <Typography variant="body2">
                                                {image.width && image.height ?
                                                    `${(image.width / image.height).toFixed(2)}:1` :
                                                    'Unknown'}
                                            </Typography>
                                        </Box>

                                        </>)}

                                        {/* Physical Properties */}
                                        {(image.length || image.max_thickness || image.softness || image.fully_insertable !== undefined) && (
                                            <>
                                                {!readOnly && (
                                                <Box sx={{ gridColumn: '1/-1', mt: 2, mb: 1 }}>
                                                    <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 'bold' }}>
                                                        Physical Properties
                                                    </Typography>
                                                </Box>
                                                )}

                                                {image.length && (
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Length
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {image.length} cm
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {image.max_thickness && (
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Max Thickness
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {image.max_thickness} cm
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {image.softness && (
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Softness
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                            {image.softness}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {image.fully_insertable !== undefined && (
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Fully Insertable
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color={image.fully_insertable ? 'success.main' : 'text.secondary'}
                                                            sx={{ fontWeight: image.fully_insertable ? 'bold' : 'normal' }}
                                                        >
                                                            {image.fully_insertable ? 'Yes' : 'No'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}

                                        {!readOnly && (<>
                                        <Box sx={{ gridColumn: '1/-1' }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Created
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDate(image.created_at)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ gridColumn: '1/-1' }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Last Modified
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDate(image.updated_at)}
                                            </Typography>
                                        </Box>
                                        </>)}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default DetailedImageView;
